import React, { useState, useEffect, useCallback } from 'react';
import {
  Box, Typography, Button, Card, CardContent, Chip, Dialog, DialogTitle,
  DialogContent, DialogActions, TextField, MenuItem, CircularProgress,
  Alert, IconButton, Tooltip, Select, FormControl, InputLabel, Grid,
  Table, TableBody, TableCell, TableContainer, TableHead, TableRow,
  FormControlLabel, Switch, Divider, Accordion, AccordionSummary,
  AccordionDetails, Paper, Stack, Menu
} from '@mui/material';
import {
  Add as AddIcon, Delete as DeleteIcon, Edit as EditIcon,
  ExpandMore as ExpandMoreIcon, PlayArrow as PlayIcon,
  Science as DryRunIcon, ContentCopy as CloneIcon,
  ArrowUpward as UpIcon, ArrowDownward as DownIcon,
  History as AuditIcon, Assessment as PositionsIcon
} from '@mui/icons-material';
import { apiRequest } from '../utils/api';
import StrategyTreeView from '../components/StrategyTreeView';
import NodeEditor from '../components/ConditionEditor';

const MODES = ['disabled', 'dry_run', 'emit'];
const WEEKDAYS = ['Mon', 'Tue', 'Wed', 'Thu', 'Fri'];

// ── Param form (auto-generated from params_schema) ──────────────────────────
function ParamForm({ spec, onChange }) {
  const schema = spec.params_schema || {};
  const keys = Object.keys(schema).length ? Object.keys(schema) : Object.keys(spec.params || {});
  const setP = (k, v) => onChange({ ...spec, params: { ...spec.params, [k]: v } });
  return (
    <Grid container spacing={1.5}>
      {keys.map(k => {
        const meta = schema[k] || {};
        const v = spec.params?.[k];
        if (meta.type === 'number[]') {
          return (
            <Grid item xs={12} sm={4} key={k}>
              <TextField fullWidth size="small" label={`${k} (comma-separated)`}
                value={Array.isArray(v) ? v.join(', ') : (v ?? '')}
                helperText={meta.description || ''}
                onChange={e => setP(k, e.target.value.split(',').map(s => parseFloat(s.trim())).filter(n => !isNaN(n)))} />
            </Grid>
          );
        }
        return (
          <Grid item xs={6} sm={3} md={2.4} key={k}>
            <TextField fullWidth size="small" type="number"
              label={meta.unit ? `${k} (${meta.unit})` : k}
              value={v ?? ''} helperText={meta.description || ''}
              inputProps={{ min: meta.min, max: meta.max, step: meta.type === 'integer' ? 1 : 'any' }}
              onChange={e => setP(k, e.target.value === '' ? undefined : +e.target.value)} />
          </Grid>
        );
      })}
    </Grid>
  );
}

// ── Indicator rows ───────────────────────────────────────────────────────────
function IndicatorsEditor({ spec, catalog, onChange }) {
  const inds = spec.indicators || [];
  const set = (i, patch) => { const n = [...inds]; n[i] = { ...n[i], ...patch }; onChange({ ...spec, indicators: n }); };
  const indNames = catalog.indicators.map(i => i.name);
  return (
    <Box>
      {inds.map((ind, i) => (
        <Box key={i} sx={{ display: 'flex', gap: 1, mb: 1, alignItems: 'center', flexWrap: 'wrap' }}>
          <TextField size="small" label="id" value={ind.id} sx={{ width: 110 }}
            onChange={e => set(i, { id: e.target.value })} />
          <Select size="small" value={ind.name} sx={{ minWidth: 130 }} displayEmpty
            onChange={e => set(i, { name: e.target.value })}>
            {[...indNames, 'level'].map(n => <MenuItem key={n} value={n}>{n}</MenuItem>)}
          </Select>
          {ind.name !== 'level' && (
            <TextField size="small" label="period" type="number" value={ind.period ?? ''} sx={{ width: 90 }}
              onChange={e => set(i, { period: +e.target.value })} />
          )}
          {ind.name === 'level' && (
            <TextField size="small" label="symbol" value={ind.symbol || ''} sx={{ width: 100 }}
              onChange={e => set(i, { symbol: e.target.value })} />
          )}
          <Select size="small" value={ind.tf || '1d'} sx={{ width: 90 }}
            onChange={e => set(i, { tf: e.target.value })}>
            {['1d', '5m', '15m', '1wk'].map(t => <MenuItem key={t} value={t}>{t}</MenuItem>)}
          </Select>
          <IconButton size="small" onClick={() => onChange({ ...spec, indicators: inds.filter((_, j) => j !== i) })}>
            <DeleteIcon fontSize="small" /></IconButton>
        </Box>
      ))}
      <Button size="small" startIcon={<AddIcon />}
        onClick={() => onChange({ ...spec, indicators: [...inds, { id: `ind${inds.length + 1}`, name: indNames[0] || 'sma', period: 20, tf: '1d' }] })}>
        Add indicator</Button>
    </Box>
  );
}

// ── Exit rule cards ──────────────────────────────────────────────────────────
const EXIT_KINDS = {
  stop: 'loss_multiple', profit_target: 'pnl_pct_of_credit',
  time_exit: 'calendar_dte', expiry: 'at_expiry',
  delta_exit: 'short_delta_gte', days_in_trade: 'gte_days', roll: 'roll',
};
const ADDABLE_EXITS = [
  { v: 'stop', cfg: { kind: 'loss_multiple', multiple_of: 2, urgency: 'urgent' } },
  { v: 'profit_target', cfg: { kind: 'pnl_pct_of_credit', pct_of: 50 } },
  { v: 'delta_exit', cfg: { kind: 'short_delta_gte', gte: 0.30, then: 'close' } },
  { v: 'days_in_trade', cfg: { kind: 'gte_days', gte: 21 } },
  { v: 'time_exit', cfg: { kind: 'calendar_dte', lte: 7, at: '15:45', then: 'close' } },
  { v: 'roll', cfg: { kind: 'roll', when: { dte_lte: 7 }, to_dte: 30, same_strike: true, credit_only: false } },
  { v: 'expiry', cfg: { kind: 'at_expiry' } },
];
function ExitsEditor({ spec, onChange }) {
  const exits = spec.exits || {};
  const prec = exits.precedence || [];
  const move = (i, dir) => {
    const p = [...prec]; const j = i + dir;
    if (j < 0 || j >= p.length) return;
    [p[i], p[j]] = [p[j], p[i]];
    onChange({ ...spec, exits: { ...exits, precedence: p } });
  };
  const paramRef = (rule, field, label) => {
    const cfg = exits[rule] || {};
    const raw = cfg[field];
    const isParam = typeof raw === 'string' && raw.startsWith('params.');
    return (
      <Box sx={{ display: 'inline-flex', gap: 0.5, alignItems: 'center' }}>
        <TextField size="small" label={label} sx={{ width: 170 }}
          value={isParam ? raw.slice(7) : (raw ?? '')}
          onChange={e => {
            const val = e.target.value;
            const next = isParam || Object.keys(spec.params || {}).includes(val)
              ? `params.${val}` : (isNaN(+val) ? val : +val);
            onChange({ ...spec, exits: { ...exits, [rule]: { ...cfg, [field]: next } } });
          }}
          helperText={isParam ? 'param reference' : 'literal number'} />
      </Box>
    );
  };
  return (
    <Box>
      {prec.map((rule, i) => {
        const cfg = exits[rule] || {};
        return (
          <Paper key={rule} variant="outlined" sx={{ p: 1.5, mb: 1, display: 'flex', gap: 1.5, alignItems: 'center', flexWrap: 'wrap' }}>
            <Chip label={`${i + 1}. ${rule}`} color="primary" variant="outlined" size="small" />
            <Typography variant="caption" color="text.secondary">{EXIT_KINDS[rule] || cfg.kind}</Typography>
            {rule === 'stop' && paramRef(rule, 'multiple_of', 'loss multiple (× credit)')}
            {rule === 'profit_target' && paramRef(rule, 'pct_of', 'pnl % of credit')}
            {rule === 'delta_exit' && (<>
              {paramRef(rule, 'gte', '|delta| ≥')}
              <Select size="small" value={cfg.then || 'close'} sx={{ width: 110 }}
                onChange={e => onChange({ ...spec, exits: { ...exits, [rule]: { ...cfg, then: e.target.value } } })}>
                <MenuItem value="close">close</MenuItem><MenuItem value="roll">roll</MenuItem>
              </Select>
            </>)}
            {rule === 'days_in_trade' && paramRef(rule, 'gte', 'days ≥')}
            {rule === 'roll' && (<>
              <Typography variant="caption">when: DTE ≤</Typography>
              <TextField size="small" type="number" sx={{ width: 70 }} value={cfg.when?.dte_lte ?? 7}
                onChange={e => onChange({ ...spec, exits: { ...exits, [rule]: { ...cfg, when: { ...cfg.when, dte_lte: +e.target.value } } } })} />
              <Typography variant="caption">or |Δ| ≥</Typography>
              <TextField size="small" type="number" sx={{ width: 80 }} value={cfg.when?.short_delta_gte ?? ''}
                onChange={e => onChange({ ...spec, exits: { ...exits, [rule]: { ...cfg, when: { ...cfg.when, short_delta_gte: e.target.value === '' ? undefined : +e.target.value } } } })} />
              <Typography variant="caption">to DTE</Typography>
              <TextField size="small" type="number" sx={{ width: 70 }} value={cfg.to_dte ?? 30}
                onChange={e => onChange({ ...spec, exits: { ...exits, [rule]: { ...cfg, to_dte: +e.target.value } } })} />
              <FormControlLabel control={<Switch size="small" checked={!cfg.same_strike}
                onChange={e => onChange({ ...spec, exits: { ...exits, [rule]: { ...cfg, same_strike: !e.target.checked } } })} />}
                label="re-strike" />
              <FormControlLabel control={<Switch size="small" checked={!!cfg.credit_only}
                onChange={e => onChange({ ...spec, exits: { ...exits, [rule]: { ...cfg, credit_only: e.target.checked } } })} />}
                label="credit only" />
            </>)}
            {rule === 'time_exit' && (<>
              {paramRef(rule, 'lte', 'calendar DTE ≤')}
              <TextField size="small" label="at (ET)" value={cfg.at || '15:45'} sx={{ width: 100 }}
                onChange={e => onChange({ ...spec, exits: { ...exits, [rule]: { ...cfg, at: e.target.value } } })} />
            </>)}
            {rule === 'expiry' && <Typography variant="caption">position closes at expiry</Typography>}
            <Select size="small" value={cfg.urgency || 'normal'} sx={{ width: 110 }}
              onChange={e => onChange({ ...spec, exits: { ...exits, [rule]: { ...cfg, urgency: e.target.value } } })}>
              <MenuItem value="normal">normal</MenuItem><MenuItem value="urgent">urgent</MenuItem>
            </Select>
            <IconButton size="small" disabled={i === 0} onClick={() => move(i, -1)}><UpIcon fontSize="small" /></IconButton>
            <IconButton size="small" disabled={i === prec.length - 1} onClick={() => move(i, 1)}><DownIcon fontSize="small" /></IconButton>
            <IconButton size="small"
              onClick={() => onChange({ ...spec, exits: { ...exits, precedence: prec.filter(r => r !== rule), [rule]: undefined } })}>
              <DeleteIcon fontSize="small" /></IconButton>
          </Paper>
        );
      })}
      <Box sx={{ display: 'flex', gap: 1, mt: 1 }}>
        {ADDABLE_EXITS.filter(e => !prec.includes(e.v)).map(e => (
          <Button key={e.v} size="small" variant="outlined" startIcon={<AddIcon />}
            onClick={() => onChange({ ...spec, exits: { ...exits, [e.v]: e.cfg, precedence: [...prec, e.v] } })}>
            {e.v}
          </Button>
        ))}
      </Box>
    </Box>
  );
}

// ── Custom structure editor (FR-ST4): up to 8 legs, generic risk math ──────
function CustomLegsEditor({ spec, onChange }) {
  const legs = spec.structure?.legs || [];
  const setLeg = (i, patch) => {
    const n = [...legs]; n[i] = { ...n[i], ...patch };
    onChange({ ...spec, structure: { ...spec.structure, legs: n } });
  };
  const addLeg = (kind) => onChange({
    ...spec,
    structure: { ...spec.structure, legs: [...legs, kind === 'stock'
      ? { kind: 'stock', side: 'BUY', qtyPerUnit: 100 }
      : { kind: 'option', right: 'P', side: 'SELL', ratio: 1, strike: 0 }] },
  });
  return (
    <Box>
      <Typography variant="caption" color="text.secondary" display="block" sx={{ mb: 1 }}>
        Custom structure — {legs.length}/8 legs. Risk math is computed generically from the legs.
      </Typography>
      {legs.map((l, i) => (
        <Paper key={i} variant="outlined" sx={{ p: 1, mb: 0.75, display: 'flex', gap: 1, alignItems: 'center', flexWrap: 'wrap' }}>
          <Chip size="small" label={`L${i + 1}`} />
          {l.kind !== 'stock' && (
            <Select size="small" value={l.right || 'P'} sx={{ width: 80 }}
              onChange={e => setLeg(i, { right: e.target.value })}>
              <MenuItem value="P">PUT</MenuItem><MenuItem value="C">CALL</MenuItem>
            </Select>
          )}
          <Select size="small" value={l.side || 'SELL'} sx={{ width: 90 }}
            onChange={e => setLeg(i, { side: e.target.value })}>
            <MenuItem value="BUY">BUY</MenuItem><MenuItem value="SELL">SELL</MenuItem>
          </Select>
          {l.kind === 'stock' ? (
            <TextField size="small" type="number" label="shares/unit" sx={{ width: 120 }}
              value={l.qtyPerUnit ?? 100}
              onChange={e => setLeg(i, { qtyPerUnit: +e.target.value })} />
          ) : (
            <>
              <TextField size="small" type="number" label="strike" sx={{ width: 110 }}
                value={l.strike ?? ''}
                onChange={e => setLeg(i, { strike: +e.target.value })} />
              <TextField size="small" type="number" label="ratio" sx={{ width: 80 }}
                value={l.ratio ?? 1} inputProps={{ min: 1, max: 8 }}
                onChange={e => setLeg(i, { ratio: +e.target.value || 1 })} />
              <TextField size="small" type="number" label="prem" sx={{ width: 90 }}
                value={l.mid ?? ''} helperText="mid (opt)"
                onChange={e => setLeg(i, { mid: e.target.value === '' ? undefined : +e.target.value })} />
            </>
          )}
          <IconButton size="small" onClick={() => onChange({
            ...spec, structure: { ...spec.structure, legs: legs.filter((_, j) => j !== i) },
          })}><DeleteIcon fontSize="small" /></IconButton>
        </Paper>
      ))}
      {legs.length < 8 && (
        <Box sx={{ display: 'flex', gap: 1 }}>
          <Button size="small" startIcon={<AddIcon />} onClick={() => addLeg('option')}>Option leg</Button>
          <Button size="small" startIcon={<AddIcon />} onClick={() => addLeg('stock')}>Stock leg</Button>
        </Box>
      )}
    </Box>
  );
}

// ── Evaluation result panel ─────────────────────────────────────────────────
function EvalResult({ result }) {
  if (!result) return null;
  const leafRows = (result.filterResults || []).flatMap(f =>
    (f.detail || []).map((d, i) => ({ filter: f.id || f.reason || '?', ...d, key: `${f.id}-${i}` })));
  const sig = result.signal;
  return (
    <Box sx={{ mt: 2 }}>
      <Box sx={{ display: 'flex', gap: 1, alignItems: 'center', flexWrap: 'wrap' }}>
        <Chip label={result.decision} color={result.decision === 'ENTER' ? 'success' : 'warning'} />
        {result.reason_code && <Chip label={result.reason_code} variant="outlined" size="small" />}
        {result.expiry && <Chip size="small" variant="outlined" label={`expiry ${result.expiry.expiry} (${result.expiry.dte}d)`} />}
        {result.widthSelection?.width && <Chip size="small" variant="outlined" label={`width ${result.widthSelection.width}`} />}
        {result.strikes && <Chip size="small" variant="outlined" label={`shorts ${result.strikes.shortPut}P / ${result.strikes.shortCall}C`} />}
      </Box>
      {leafRows.length > 0 && (
        <TableContainer component={Paper} variant="outlined" sx={{ mt: 1.5 }}>
          <Table size="small">
            <TableHead><TableRow>
              <TableCell>Filter</TableCell><TableCell>Comparator</TableCell>
              <TableCell>Observed</TableCell><TableCell>Pass</TableCell><TableCell>Skip reason</TableCell>
            </TableRow></TableHead>
            <TableBody>
              {leafRows.map(r => (
                <TableRow key={r.key}>
                  <TableCell>{r.filter}</TableCell><TableCell>{r.cmp}</TableCell>
                  <TableCell sx={{ fontFamily: 'monospace', fontSize: 12 }}>
                    {(() => { const v = r.values || {};
                      const obs = v.a ?? v.aNow ?? (v.vals || [])[0];
                      const bound = v.b ?? (v.lo != null ? `[${v.lo}–${v.hi}]` : v.bNow);
                      return `${obs ?? '?'}${bound != null ? ` vs ${Array.isArray(bound) ? bound : bound}` : ''}`;
                    })()}
                  </TableCell>
                  <TableCell>{r.pass ? '✓' : '✗'}</TableCell>
                  <TableCell>{r.pass ? '' : (r.skip_reason || 'FAIL')}</TableCell>
                </TableRow>
              ))}
            </TableBody>
          </Table>
        </TableContainer>
      )}
      {result.widthSelection?.candidates?.length > 0 && (
        <Box sx={{ mt: 1 }}>
          <Typography variant="caption" color="text.secondary">
            Width candidates: {result.widthSelection.candidates.map(c =>
              `${c.width}w ${c.verdict}${c.net_credit != null ? ` ($${c.net_credit}, ${c.credit_pct_of_width}%)` : ''}${c.contracts_sizable != null ? ` fits ${c.contracts_sizable}` : ''}`
            ).join(' · ')}
            {result.widthSelection.fallback ? ' — fallback width used' : ''}
          </Typography>
        </Box>
      )}
      {sig && (
        <Accordion sx={{ mt: 1 }}>
          <AccordionSummary expandIcon={<ExpandMoreIcon />}>
            <Typography variant="body2">Signal: {sig.signal_id} — {sig.structure?.legs?.length} legs, est credit {sig.structure?.est_net_credit}</Typography>
          </AccordionSummary>
          <AccordionDetails>
            <pre style={{ fontSize: 11, overflow: 'auto', margin: 0 }}>{JSON.stringify(sig, null, 2)}</pre>
          </AccordionDetails>
        </Accordion>
      )}
    </Box>
  );
}

// ── Run result panel ─────────────────────────────────────────────────────────
function RunResult({ result }) {
  if (!result) return null;
  return (
    <Box sx={{ mt: 2 }}>
      {(result.steps || []).map((s, i) => (
        <Paper key={i} variant="outlined" sx={{ p: 1, mb: 0.75 }}>
          <Typography variant="caption" sx={{ fontWeight: 600 }}>{s.name}</Typography>
          <pre style={{ fontSize: 11, overflow: 'auto', margin: 0 }}>{JSON.stringify({ ...s, name: undefined, steps: undefined }, null, 1).slice(0, 1500)}</pre>
        </Paper>
      ))}
    </Box>
  );
}

// ── Main page ────────────────────────────────────────────────────────────────
export default function StrategyBuilder() {
  const [catalog, setCatalog] = useState({ indicators: [], comparators: [], templates: [], contextValues: [], builtins: {} });
  const [strategies, setStrategies] = useState([]);
  const [instances, setInstances] = useState([]);
  const [tokens, setTokens] = useState([]);
  const [accounts, setAccounts] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [successMsg, setSuccessMsg] = useState(null);

  // builder state
  const [detail, setDetail] = useState(null);        // { strategy, versions, parameterSets, instances }
  const [draft, setDraft] = useState(null);          // working spec
  const [draftName, setDraftName] = useState('');
  const [draftVersion, setDraftVersion] = useState('');
  const [validating, setValidating] = useState(null);
  const [saving, setSaving] = useState(false);

  // instance dialog state
  const [instDialog, setInstDialog] = useState(false);
  const [instForm, setInstForm] = useState({});

  // eval/run results
  const [evalResult, setEvalResult] = useState(null);
  const [runResult, setRunResult] = useState(null);
  const [busyInst, setBusyInst] = useState(null);
  const [evalDate, setEvalDate] = useState('');

  // v6 tree view + Run Test overlay
  const [tree, setTree] = useState(null);        // { tree, migrated, report, coverage }
  const [treeTest, setTreeTest] = useState(null); // last evaluate() result w/ trace
  const [treeInstId, setTreeInstId] = useState('');
  const [treeBt, setTreeBt] = useState(null);    // backtest report (FR-R4)
  const [equiv, setEquiv] = useState(null);      // equivalence job result (FR-F3)
  const [equivJob, setEquivJob] = useState(null);

  // audit dialog
  const [audit, setAudit] = useState(null); // { type: 'evaluations'|'signals'|'positions', rows, title }

  // import/export (v5 §5.6)
  const [newMenuAnchor, setNewMenuAnchor] = useState(null);
  const [importOpen, setImportOpen] = useState(false);
  const [importText, setImportText] = useState('');
  const [importPreview, setImportPreview] = useState(null); // dry-run result
  const [importBusy, setImportBusy] = useState(false);

  const loadAll = useCallback(async () => {
    try {
      setLoading(true);
      const [cat, strats, insts, toks, accts] = await Promise.all([
        apiRequest('/api/strategies/catalog'),
        apiRequest('/api/strategies'),
        apiRequest('/api/strategies/instances/list'),
        apiRequest('/api/tokens'),
        apiRequest('/api/accounts'),
      ]);
      if (cat.status === 200) setCatalog(cat.data);
      if (strats.status === 200) setStrategies(strats.data.strategies || []);
      if (insts.status === 200) setInstances(insts.data.instances || []);
      if (toks.status === 200) setTokens(toks.data.data || []);
      if (accts.status === 200) {
        const all = Array.isArray(accts.data) ? accts.data : (accts.data.data || accts.data || []);
        setAccounts(all.filter(a => a.apiType || a.api_type));
      }
      setError(null);
    } catch (e) { setError(e.message); }
    finally { setLoading(false); }
  }, []);

  useEffect(() => { loadAll(); }, [loadAll]);

  const known = {
    indicatorIds: (draft?.indicators || []).map(i => i.id),
    paramNames: Object.keys({ ...(draft?.params_schema || {}), ...(draft?.params || {}) }),
    contextValues: catalog.contextValues || [],
  };

  // Template-driven UI (FR-ST3): inputs/anchors/risk requirements come from
  // catalog.templateMeta — never hardcoded per template.
  const templateMeta = (catalog.templateMeta || []).find(t => t.id === draft?.structure?.template) || null;
  const inputs = templateMeta?.inputs || [];
  const needsWidth = inputs.includes('width');
  const needsWingWidths = inputs.includes('widthDown') || inputs.includes('widthUp');
  const needsExpiry2 = inputs.includes('expiry2');
  const isCustom = draft?.structure?.template === 'custom';
  const isUndefinedRisk = templateMeta?.requires?.undefined_risk === true;
  const riskBasisType = draft?.structure?.risk_basis?.type || templateMeta?.defaultRiskBasis?.type || 'max_loss';
  const hasRiskExit = Object.keys(draft?.exits || {}).some(k => ['stop', 'delta_exit', 'time_exit', 'roll'].includes(k));
  const underlyingMode = draft?.underlying?.mode === 'symbols' ? 'symbols'
    : draft?.underlying?.mode === 'universe' ? 'universe' : 'single';
  const ackNoStop = (draft?.acknowledgments || []).some(a => (typeof a === 'string' ? a : a?.id) === 'undefined_risk_no_stop');

  const openStrategy = async (id) => {
    const res = await apiRequest(`/api/strategies/${id}`);
    if (res.status !== 200) { setError(res.data?.error || 'load failed'); return; }
    setDetail(res.data);
    const ver = res.data.versions?.[0];
    if (ver) {
      const specRes = await apiRequest(`/api/strategies/${id}/versions/${ver.id}/spec`);
      if (specRes.status === 200) {
        setDraft(JSON.parse(JSON.stringify(specRes.data.version.spec)));
        setDraftName(res.data.strategy.name);
        setDraftVersion(ver.version);
      }
      const treeRes = await apiRequest(`/api/strategies/${id}/tree`);
      setTree(treeRes.status === 200 ? treeRes.data : null);
      setTreeTest(null);
      setEquiv(null); setEquivJob(null);
      setTreeInstId(res.data.instances?.[0]?.id ?? '__defaults__');
    }
  };

  const runTreeTest = async () => {
    if (!treeInstId) { setError('pick an instance or defaults to test'); return; }
    setBusyInst(-1); setTreeTest(null);
    const res = treeInstId === '__defaults__'
      ? await apiRequest(`/api/strategies/${detail.strategy.id}/evaluate`, {
          method: 'POST',
          body: JSON.stringify({ ...(evalDate ? { date: evalDate } : {}) }),
        })
      : await apiRequest(`/api/strategies/instances/${treeInstId}/evaluate`, {
          method: 'POST',
          body: JSON.stringify({ test: true, as_tree: true, ...(evalDate ? { date: evalDate } : {}) }),
        });
    setBusyInst(null);
    if (res.status === 200) setTreeTest(res.data);
    else setError(res.data?.error || 'test run failed');
  };

  // FR-F3 equivalence badge: spec vs migrated tree, day-by-day replay. Runs
  // as a server job — poll until done.
  const runEquivalence = async () => {
    setEquiv(null); setBusyInst(-3);
    const res = await apiRequest(`/api/strategies/${detail.strategy.id}/tree/equivalence`, {
      method: 'POST', body: JSON.stringify({ days: 60 }),
    });
    if (res.status !== 200 || !res.data.job_id) {
      setBusyInst(null);
      if (res.data?.status === 'n/a') setEquiv({ status: 'n/a', reason: res.data.reason });
      else setError(res.data?.error || 'equivalence check failed to start');
      return;
    }
    const jobId = res.data.job_id;
    const poll = async () => {
      const j = await apiRequest(`/api/strategies/${detail.strategy.id}/tree/equivalence/${jobId}`);
      if (j.data?.status === 'running') {
        setEquivJob(j.data);
        setTimeout(poll, 4000);
        return;
      }
      setBusyInst(null); setEquivJob(null);
      if (j.data?.status === 'done') setEquiv(j.data.result);
      else setError(j.data?.error || 'equivalence check failed');
    };
    poll();
  };

  // WI-7 range test — evaluate the last N trading days at entry_time via the
  // /range-test route; rows link back into single-day replay; an oracle CSV
  // upload produces the diff (matches / mismatches / borderline).
  const [oracleFile, setOracleFile] = useState(null);
  const runTreeBacktest = async () => {
    if (!treeInstId) { setError('pick an instance to test'); return; }
    setBusyInst(-2); setTreeBt(null);
    let oracleCsv = null;
    if (oracleFile) {
      try { oracleCsv = await oracleFile.text(); } catch { oracleCsv = null; }
    }
    const res = await apiRequest(`/api/strategies/instances/${treeInstId}/range-test`, {
      method: 'POST', body: JSON.stringify({ days: 30, oracle_csv: oracleCsv }),
    });
    setBusyInst(null);
    if (res.status === 200) setTreeBt(res.data);
    else setError(res.data?.error || 'range test failed');
  };

  const replayDay = async (day) => {
    setBusyInst(-1); setTreeTest(null);
    const res = treeInstId === '__defaults__'
      ? await apiRequest(`/api/strategies/${detail.strategy.id}/evaluate`, {
          method: 'POST', body: JSON.stringify({ date: day }),
        })
      : await apiRequest(`/api/strategies/instances/${treeInstId}/evaluate`, {
          method: 'POST', body: JSON.stringify({ test: true, as_tree: true, date: day }),
        });
    setBusyInst(null);
    if (res.status === 200) setTreeTest(res.data);
    else setError(res.data?.error || 'replay failed');
  };

  const newFromTemplate = async (templateId) => {
    const res = await apiRequest('/api/strategies/from-template', {
      method: 'POST', body: JSON.stringify({ template: templateId }),
    });
    if (res.status !== 201) { setError(res.data?.error || 'create failed'); return; }
    setSuccessMsg(`Strategy '${res.data.strategy.name}' created from template`);
    await loadAll();
    openStrategy(res.data.strategy.id);
  };

  // Blank start (v6 FR-U3): a minimal tree with the standard guards — build
  // everything else with "+" in the decision-tree editor.
  const newBlank = async () => {
    const name = window.prompt('Strategy name', 'new strategy');
    if (!name) return;
    const spec = {
      strategy: { id: name.toLowerCase().replace(/[^a-z0-9]+/g, '_') || 'blank', version: '1.0.0' },
      underlying: { symbol: 'SPX' },
      schedule: { calendar: 'NYSE', entry_time: '10:00', max_entries_per_day: 1 },
      inputs: [
        { name: 'symbol', type: 'symbol', default: 'SPX' },
        { name: 'open_new_positions', type: 'switch', default: true },
        { name: 'exit_options', type: 'exit_options', default: { profit_target_pct: 50, stop_multiple_of_credit: 1.5 } },
      ],
      flows: [{
        id: 'entry', type: 'entry',
        trigger: { type: 'trigger.scheduled', days: ['Mon', 'Tue', 'Wed', 'Thu', 'Fri'], time: '10:00', tz: 'America/New_York' },
        steps: [
          { id: 's1', type: 'decision.switch', input: 'open_new_positions',
            yes: [{ id: 's2', type: 'decision.can_open_new_position',
              yes: [],
              no: [{ id: 's4', type: 'notify', reason: 'CANNOT_OPEN', message: 'The bot cannot open a new position.' }] }],
            no: [{ id: 's3', type: 'notify', reason: 'SWITCH_OFF', message: 'Open New Positions switch is off.' }] },
        ],
      }],
    };
    const res = await apiRequest('/api/strategies', {
      method: 'POST', body: JSON.stringify({ name, spec, version: '1.0.0' }),
    });
    if (res.status !== 201) { setError(res.data?.errors ? JSON.stringify(res.data.errors) : (res.data?.error || 'create failed')); return; }
    setSuccessMsg(`Strategy '${name}' created — open the Decision tree and press Edit to add steps`);
    await loadAll();
    openStrategy(res.data.strategy.id);
  };

  const validateDraft = async () => {
    const res = await apiRequest('/api/strategies/validate', { method: 'POST', body: JSON.stringify({ spec: draft }) });
    setValidating(res.status === 200 ? res.data : { valid: false, errors: [{ error: res.data?.error || 'validation failed' }] });
  };

  const saveDraft = async () => {
    if (!detail) return;
    setSaving(true);
    const v = bumpVersion(draftVersion);
    const res = await apiRequest(`/api/strategies/${detail.strategy.id}/versions`, {
      method: 'POST', body: JSON.stringify({ version: v, spec: draft }),
    });
    setSaving(false);
    if (res.status !== 201) { setError(res.data?.errors ? JSON.stringify(res.data.errors) : (res.data?.error || 'save failed')); return; }
    setSuccessMsg(`Saved version ${v}`);
    setDraftVersion(v);
    openStrategy(detail.strategy.id);
  };

  const saveInstance = async () => {
    const res = await apiRequest(instForm.id ? `/api/strategies/instances/${instForm.id}` : '/api/strategies/instances', {
      method: instForm.id ? 'PATCH' : 'POST', body: JSON.stringify(instForm),
    });
    if (![200, 201].includes(res.status)) { setError(res.data?.error || 'save failed'); return; }
    setInstDialog(false);
    setSuccessMsg(instForm.id ? 'Instance updated' : 'Instance created (defaults to DRY_RUN)');
    loadAll();
    if (detail) openStrategy(detail.strategy.id);
  };

  const patchInstance = async (id, patch) => {
    const res = await apiRequest(`/api/strategies/instances/${id}`, { method: 'PATCH', body: JSON.stringify(patch) });
    if (res.status !== 200) { setError(res.data?.error || 'update failed'); return; }
    loadAll(); if (detail) openStrategy(detail.strategy.id);
  };

  const deleteStrategy = async (s) => {
    if (!window.confirm(`Delete strategy '${s.name}' and all its versions, instances, signals, and positions?`)) return;
    const res = await apiRequest(`/api/strategies/${s.id}`, { method: 'DELETE' });
    if (res.status !== 200) { setError(res.data?.error || 'delete failed'); return; }
    if (detail?.strategy?.id === s.id) { setDetail(null); setDraft(null); }
    loadAll();
  };

  const deleteInstance = async (inst) => {
    if (!window.confirm(`Delete instance '${inst.name}' and its signals, positions, and evaluation history?`)) return;
    const res = await apiRequest(`/api/strategies/instances/${inst.id}`, { method: 'DELETE' });
    if (res.status !== 200) { setError(res.data?.error || 'delete failed'); return; }
    loadAll(); if (detail) openStrategy(detail.strategy.id);
  };

  const doEvaluate = async (instId) => {
    setBusyInst(instId); setEvalResult(null); setRunResult(null);
    const res = await apiRequest(`/api/strategies/instances/${instId}/evaluate`, {
      method: 'POST', body: JSON.stringify(evalDate ? { date: evalDate } : {}),
    });
    setBusyInst(null);
    setEvalResult(res.status === 200 ? res.data : { decision: 'ERROR', reason_code: res.data?.error });
  };

  const doRun = async (instId, dry) => {
    setBusyInst(instId); setRunResult(null); setEvalResult(null);
    const res = await apiRequest(`/api/strategies/instances/${instId}/run`, {
      method: 'POST', body: JSON.stringify({ dry }),
    });
    setBusyInst(null);
    setRunResult(res.status === 200 ? res.data : { error: res.data?.error });
  };

  // ── JSON export/import (FR-J12/J13) ──────────────────────────────────────
  const doExport = async () => {
    if (!detail?.strategy) return;
    const res = await apiRequest(`/api/strategies/${detail.strategy.id}/export`);
    if (res.status !== 200) { setError(res.data?.error || 'export failed'); return; }
    const doc = res.data;
    const blob = new Blob([JSON.stringify(doc, null, 2)], { type: 'application/json' });
    const a = document.createElement('a');
    a.href = URL.createObjectURL(blob);
    a.download = `${doc.strategy.id}_${doc.strategy.version}.strategy.json`;
    a.click();
    URL.revokeObjectURL(a.href);
    setSuccessMsg(`Exported ${doc.strategy.id} v${doc.strategy.version} (hash ${String(doc.content_hash).slice(7, 19)}…)`);
  };

  const doImportPreview = async (text) => {
    setImportBusy(true);
    const res = await apiRequest('/api/strategies/import?dry_run=1', {
      method: 'POST', body: JSON.stringify({ document: text, filename: 'paste' }),
    });
    setImportBusy(false);
    setImportPreview(res.data);
    return res.data;
  };

  const doImportCommit = async (conflict) => {
    setImportBusy(true);
    const res = await apiRequest('/api/strategies/import', {
      method: 'POST', body: JSON.stringify({ document: importText, filename: 'paste', conflict }),
    });
    setImportBusy(false);
    if (!res.data?.ok) { setImportPreview(res.data); return; }
    setSuccessMsg(`Imported as '${res.data.strategy?.name}' v${res.data.version?.version} — draft, unattached, DRY_RUN`);
    setImportOpen(false); setImportText(''); setImportPreview(null);
    loadAll();
  };

  const openAudit = async (inst, type) => {
    const res = await apiRequest(`/api/strategies/instances/${inst.id}/${type}?limit=50`);
    setAudit({ type, rows: res.data?.[type] || [], title: `${inst.name} — ${type}` });
  };

  if (loading) return <Box sx={{ p: 3, display: 'flex', justifyContent: 'center' }}><CircularProgress /></Box>;

  return (
    <Box sx={{ p: 3 }}>
      <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 2 }}>
        <Box />
        <Box>
          <Button variant="outlined" size="small" sx={{ ml: 1 }}
            onClick={() => { setImportOpen(true); setImportPreview(null); setImportText(''); }}>
            Import JSON</Button>
          {detail?.strategy && (
            <Button variant="outlined" size="small" sx={{ ml: 1 }} onClick={doExport}>
              Export JSON</Button>
          )}
          <Button variant="contained" size="small" sx={{ ml: 1 }} endIcon={<ExpandMoreIcon />}
            onClick={e => setNewMenuAnchor(e.currentTarget)}>
            New strategy</Button>
          <Menu anchorEl={newMenuAnchor} open={!!newMenuAnchor} onClose={() => setNewMenuAnchor(null)}>
            <MenuItem onClick={() => { setNewMenuAnchor(null); newBlank(); }}>
              <Typography variant="body2" fontWeight={600}>blank</Typography>
              <Typography variant="caption" color="text.secondary" sx={{ ml: 1 }}>— build in the tree editor</Typography>
            </MenuItem>
            <Divider />
            {Object.keys(catalog.builtins || {}).map(t => (
              <MenuItem key={t} onClick={() => { setNewMenuAnchor(null); newFromTemplate(t); }}>
                <CloneIcon fontSize="small" sx={{ mr: 1 }} /> {t}
              </MenuItem>
            ))}
          </Menu>
        </Box>
      </Box>

      {error && <Alert severity="error" sx={{ mb: 2 }} onClose={() => setError(null)}>{error}</Alert>}
      {successMsg && <Alert severity="success" sx={{ mb: 2 }} onClose={() => setSuccessMsg(null)}>{successMsg}</Alert>}
      {validating && (
        <Alert severity={validating.valid ? 'success' : 'warning'} sx={{ mb: 2 }} onClose={() => setValidating(null)}>
          {validating.valid ? 'Spec valid' : (validating.errors || []).map(e => `${e.path}: ${e.error}`).join(' | ')}
        </Alert>
      )}

      {/* ── Strategies list ─────────────────────────────────────────── */}
      <Card sx={{ mb: 3 }}>
        <CardContent>
          <Typography variant="h6" gutterBottom>Strategies</Typography>
          <TableContainer>
            <Table size="small">
              <TableHead><TableRow>
                <TableCell>Name</TableCell><TableCell>Created</TableCell><TableCell></TableCell>
              </TableRow></TableHead>
              <TableBody>
                {strategies.map(s => (
                  <TableRow key={s.id} hover selected={detail?.strategy?.id === s.id}
                    sx={{ cursor: 'pointer' }} onClick={() => openStrategy(s.id)}>
                    <TableCell>{s.name}</TableCell>
                    <TableCell>{new Date(s.created_at).toLocaleDateString()}</TableCell>
                    <TableCell align="right">
                      <EditIcon fontSize="small" />
                      <IconButton size="small" title="Delete strategy"
                        onClick={e => { e.stopPropagation(); deleteStrategy(s); }}>
                        <DeleteIcon fontSize="small" />
                      </IconButton>
                    </TableCell>
                  </TableRow>
                ))}
                {!strategies.length && <TableRow><TableCell colSpan={3}>
                  <Typography color="text.secondary">No strategies yet — create one from a template above.</Typography>
                </TableCell></TableRow>}
              </TableBody>
            </Table>
          </TableContainer>
        </CardContent>
      </Card>

      {/* ── Spec builder ────────────────────────────────────────────── */}
      {draft && detail && (
        <Card sx={{ mb: 3 }}>
          <CardContent>
            <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 1 }}>
              <Typography variant="h6">{draftName} — spec editor <Chip size="small" label={`v${draftVersion}`} /></Typography>
              <Box>
                <Button size="small" onClick={validateDraft}>Validate</Button>
                <Button size="small" variant="contained" disabled={saving} onClick={saveDraft} sx={{ ml: 1 }}>
                  {saving ? 'Saving…' : 'Save as new version'}</Button>
              </Box>
            </Box>
            <Typography variant="caption" color="text.secondary" display="block" sx={{ mb: 2 }}>
              Versions are immutable — saving creates a new version; instances pin whichever version they reference.
            </Typography>

            <Accordion defaultExpanded>
              <AccordionSummary expandIcon={<ExpandMoreIcon />}><Typography fontWeight={600}>Basics & Schedule</Typography></AccordionSummary>
              <AccordionDetails>
                <Grid container spacing={1.5}>
                  {/* FR-ST6: fixed symbol list or named universe */}
                  <Grid item xs={4} sm={2}>
                    <FormControl fullWidth size="small"><InputLabel>underlyings</InputLabel>
                      <Select label="underlyings" value={underlyingMode}
                        onChange={e => {
                          const m = e.target.value;
                          const u = { ...draft.underlying };
                          if (m === 'single') { u.mode = undefined; u.symbols = undefined; u.universe = undefined; u.symbol = u.symbol || 'SPX'; }
                          if (m === 'symbols') { u.mode = 'symbols'; u.symbols = u.symbols?.length ? u.symbols : [u.symbol || 'SPY']; u.universe = undefined; }
                          if (m === 'universe') { u.mode = 'universe'; u.universe = 'watchlist'; u.symbols = undefined; }
                          setDraft({ ...draft, underlying: u });
                        }}>
                        <MenuItem value="single">single symbol</MenuItem>
                        <MenuItem value="symbols">symbol list</MenuItem>
                        <MenuItem value="universe">universe (watchlist)</MenuItem>
                      </Select></FormControl>
                  </Grid>
                  {underlyingMode === 'single' && (
                    <Grid item xs={4} sm={2}><TextField fullWidth size="small" label="underlying" value={draft.underlying?.symbol || ''}
                      onChange={e => setDraft({ ...draft, underlying: { ...draft.underlying, symbol: e.target.value } })} /></Grid>
                  )}
                  {underlyingMode === 'symbols' && (
                    <Grid item xs={8} sm={4}><TextField fullWidth size="small" label="symbols (comma-separated)"
                      value={(draft.underlying?.symbols || []).join(', ')}
                      onChange={e => setDraft({ ...draft, underlying: { ...draft.underlying,
                        symbols: e.target.value.split(',').map(s => s.trim().toUpperCase()).filter(Boolean) } })} /></Grid>
                  )}
                  {underlyingMode === 'universe' && (
                    <Grid item xs={4} sm={2}><TextField fullWidth size="small" label="universe" value={draft.underlying?.universe || 'watchlist'}
                      onChange={e => setDraft({ ...draft, underlying: { ...draft.underlying, universe: e.target.value } })} /></Grid>
                  )}
                  {(underlyingMode !== 'single') && (
                    <>
                      <Grid item xs={4} sm={2}><TextField fullWidth size="small" type="number" label="max trades/run" value={draft.scanner?.max_trades_per_run ?? 1}
                        onChange={e => setDraft({ ...draft, scanner: { ...draft.scanner, max_trades_per_run: +e.target.value } })} /></Grid>
                      <Grid item xs={4} sm={2}>
                        <FormControl fullWidth size="small"><InputLabel>rank by</InputLabel>
                          <Select label="rank by" value={draft.scanner?.rank_by || 'net_credit'}
                            onChange={e => setDraft({ ...draft, scanner: { ...draft.scanner, rank_by: e.target.value } })}>
                            <MenuItem value="net_credit">net credit</MenuItem>
                            <MenuItem value="credit_pct">credit % of max loss</MenuItem>
                            <MenuItem value="risk_basis_value">risk basis value</MenuItem>
                          </Select></FormControl>
                      </Grid>
                    </>
                  )}
                  <Grid item xs={4} sm={2}><TextField fullWidth size="small" label="chain root" value={draft.underlying?.chain_root || ''}
                    onChange={e => setDraft({ ...draft, underlying: { ...draft.underlying, chain_root: e.target.value } })} /></Grid>
                  <Grid item xs={4} sm={2}>
                    <Select fullWidth size="small" value={draft.underlying?.settlement || 'PM'}
                      onChange={e => setDraft({ ...draft, underlying: { ...draft.underlying, settlement: e.target.value } })}>
                      <MenuItem value="PM">PM settle</MenuItem><MenuItem value="AM">AM settle</MenuItem>
                    </Select></Grid>
                  <Grid item xs={4} sm={2}><TextField fullWidth size="small" label="entry time (ET)" value={draft.schedule?.entry_time || ''}
                    onChange={e => setDraft({ ...draft, schedule: { ...draft.schedule, entry_time: e.target.value } })} /></Grid>
                  <Grid item xs={4} sm={2}><TextField fullWidth size="small" type="number" label="max entries/day" value={draft.schedule?.max_entries_per_day ?? 1}
                    onChange={e => setDraft({ ...draft, schedule: { ...draft.schedule, max_entries_per_day: +e.target.value } })} /></Grid>
                  <Grid item xs={4} sm={2}><TextField fullWidth size="small" type="number" label="TTL (sec)" value={draft.signal_ttl_seconds ?? 300}
                    onChange={e => setDraft({ ...draft, signal_ttl_seconds: +e.target.value })} /></Grid>
                </Grid>
              </AccordionDetails>
            </Accordion>

            <Accordion>
              <AccordionSummary expandIcon={<ExpandMoreIcon />}><Typography fontWeight={600}>Parameters</Typography></AccordionSummary>
              <AccordionDetails><ParamForm spec={draft} onChange={setDraft} /></AccordionDetails>
            </Accordion>

            <Accordion>
              <AccordionSummary expandIcon={<ExpandMoreIcon />}><Typography fontWeight={600}>Indicators ({(draft.indicators || []).length})</Typography></AccordionSummary>
              <AccordionDetails><IndicatorsEditor spec={draft} catalog={catalog} onChange={setDraft} /></AccordionDetails>
            </Accordion>

            <Accordion>
              <AccordionSummary expandIcon={<ExpandMoreIcon />}><Typography fontWeight={600}>Entry filters ({(draft.filters || []).length})</Typography></AccordionSummary>
              <AccordionDetails>
                {(draft.filters || []).map((f, i) => (
                  <Box key={i} sx={{ mb: 1.5 }}>
                    <Box sx={{ display: 'flex', gap: 1, alignItems: 'center', mb: 0.5 }}>
                      <TextField size="small" label="filter id" value={f.id || ''} sx={{ width: 150 }}
                        onChange={e => { const fs = [...draft.filters]; fs[i] = { ...f, id: e.target.value }; setDraft({ ...draft, filters: fs }); }} />
                      <TextField size="small" label="skip code" value={f.skip || ''} sx={{ width: 190 }}
                        onChange={e => { const fs = [...draft.filters]; fs[i] = { ...f, skip: e.target.value }; setDraft({ ...draft, filters: fs }); }} />
                      <IconButton size="small" onClick={() => setDraft({ ...draft, filters: draft.filters.filter((_, j) => j !== i) })}>
                        <DeleteIcon fontSize="small" /></IconButton>
                    </Box>
                    <NodeEditor node={f} known={known}
                      onChange={v => { const fs = [...draft.filters]; fs[i] = { ...v, id: f.id, skip: f.skip }; setDraft({ ...draft, filters: fs }); }} />
                  </Box>
                ))}
                <Button size="small" startIcon={<AddIcon />}
                  onClick={() => setDraft({ ...draft, filters: [...(draft.filters || []),
                    { id: `FILTER_${(draft.filters || []).length + 1}`, skip: 'FILTER_FAIL', op: 'all',
                      conditions: [{ lhs: { context: 'vix_open' }, cmp: '<=', rhs: { value: 0 } }] }] })}>
                  Add filter</Button>
              </AccordionDetails>
            </Accordion>

            <Accordion>
              <AccordionSummary expandIcon={<ExpandMoreIcon />}><Typography fontWeight={600}>Expiry & Strike selection</Typography></AccordionSummary>
              <AccordionDetails>
                <Grid container spacing={1.5}>
                  <Grid item xs={12} sm={4}>
                    <FormControl fullWidth size="small"><InputLabel>expiry selector</InputLabel>
                      <Select label="expiry selector" value={draft.expiry?.selector || ''}
                        onChange={e => setDraft({ ...draft, expiry: { ...draft.expiry, selector: e.target.value } })}>
                        {(catalog.expirySelectors || []).map(s => <MenuItem key={s} value={s}>{s}</MenuItem>)}
                      </Select></FormControl>
                  </Grid>
                  {draft.expiry?.selector === 'weekday_target_dte' && WEEKDAYS.map(d => (
                    <Grid item xs={4} sm={1.5} key={d}>
                      <TextField fullWidth size="small" type="number" label={`${d} DTE`} value={draft.expiry?.map?.[d] ?? ''}
                        onChange={e => setDraft({ ...draft, expiry: { ...draft.expiry, map: { ...draft.expiry.map, [d]: +e.target.value } } })} />
                    </Grid>
                  ))}
                  <Grid item xs={12} sm={4}>
                    <FormControl fullWidth size="small"><InputLabel>strike selector</InputLabel>
                      <Select label="strike selector" value={draft.strikes?.selector || ''}
                        onChange={e => setDraft({ ...draft, strikes: { ...draft.strikes, selector: e.target.value } })}>
                        {(catalog.strikeSelectors || []).map(s => <MenuItem key={s} value={s}>{s}</MenuItem>)}
                      </Select></FormControl>
                  </Grid>
                  <Grid item xs={4} sm={2}><TextField fullWidth size="small" type="number" label="increment" value={draft.strikes?.increment ?? 5}
                    onChange={e => setDraft({ ...draft, strikes: { ...draft.strikes, increment: +e.target.value } })} /></Grid>
                </Grid>
              </AccordionDetails>
            </Accordion>

            <Accordion>
              <AccordionSummary expandIcon={<ExpandMoreIcon />}>
                <Typography fontWeight={600}>Structure &amp; Risk basis</Typography>
                {templateMeta?.riskProfile === 'undefined' && (
                  <Chip size="small" color="error" label="undefined risk" sx={{ ml: 1 }} />)}
                {templateMeta?.riskProfile === 'collateral' && (
                  <Chip size="small" color="info" label="collateral" sx={{ ml: 1 }} />)}
              </AccordionSummary>
              <AccordionDetails>
                <Grid container spacing={1.5}>
                  <Grid item xs={12} sm={4}>
                    <FormControl fullWidth size="small"><InputLabel>template</InputLabel>
                      <Select label="template" value={draft.structure?.template || ''}
                        onChange={e => setDraft({ ...draft, structure: { ...draft.structure, template: e.target.value } })}>
                        {(catalog.templateMeta || catalog.templates?.map(t => ({ id: t })) || []).map(t => (
                          <MenuItem key={t.id || t} value={t.id || t}>{t.label || t.id || t}</MenuItem>))}
                      </Select></FormControl>
                    {templateMeta?.description && (
                      <Typography variant="caption" color="text.secondary">{templateMeta.description}</Typography>)}
                  </Grid>
                  {templateMeta?.anchors?.length > 0 && (
                    <Grid item xs={12} sm={4}>
                      <Typography variant="caption" color="text.secondary" display="block">strike anchors required</Typography>
                      {templateMeta.anchors.map(a => <Chip key={a} size="small" variant="outlined" label={a} sx={{ mr: 0.5, mt: 0.5 }} />)}
                    </Grid>
                  )}

                  {/* FR-ST3: only the inputs this template actually needs. */}
                  {needsWidth && (
                    <>
                      <Grid item xs={6} sm={3}>
                        <FormControl fullWidth size="small"><InputLabel>width policy</InputLabel>
                          <Select label="width policy" value={draft.structure?.width_selection?.policy || 'widest_fit'}
                            onChange={e => setDraft({ ...draft, structure: { ...draft.structure, width_selection: { ...draft.structure?.width_selection, policy: e.target.value } } })}>
                            <MenuItem value="widest_fit">widest fit (risk context)</MenuItem>
                            <MenuItem value="narrowest_fit">narrowest fit</MenuItem>
                            <MenuItem value="fixed">fixed</MenuItem>
                          </Select></FormControl>
                      </Grid>
                      <Grid item xs={6} sm={3}>
                        <FormControl fullWidth size="small"><InputLabel>width candidates</InputLabel>
                          <Select label="width candidates" value={draft.structure?.width_selection?.candidates || ''}
                            onChange={e => setDraft({ ...draft, structure: { ...draft.structure, width_selection: { ...draft.structure?.width_selection, candidates: e.target.value } } })}>
                            {Object.keys(draft.params_schema || {}).filter(k => draft.params_schema[k].type === 'number[]')
                              .map(k => <MenuItem key={k} value={`params.${k}`}>params.{k}</MenuItem>)}
                          </Select></FormControl>
                      </Grid>
                      <Grid item xs={6} sm={3}>
                        <TextField fullWidth size="small" type="number" label="fallback width" value={draft.structure?.width_selection?.fallback_width ?? ''}
                          onChange={e => setDraft({ ...draft, structure: { ...draft.structure, width_selection: { ...draft.structure?.width_selection, fallback_width: +e.target.value } } })} />
                      </Grid>
                    </>
                  )}
                  {needsWingWidths && (
                    <>
                      <Grid item xs={6} sm={3}>
                        <TextField fullWidth size="small" type="number" label="wing width (down)" value={draft.structure?.width_down ?? ''}
                          onChange={e => setDraft({ ...draft, structure: { ...draft.structure, width_down: +e.target.value } })} />
                      </Grid>
                      <Grid item xs={6} sm={3}>
                        <TextField fullWidth size="small" type="number" label="wing width (up)" value={draft.structure?.width_up ?? ''}
                          onChange={e => setDraft({ ...draft, structure: { ...draft.structure, width_up: +e.target.value } })} />
                      </Grid>
                    </>
                  )}
                  {needsExpiry2 && (
                    <Grid item xs={6} sm={3}>
                      <TextField fullWidth size="small" type="number" label="far expiry target DTE" value={draft.structure?.expiry2_dte ?? 60}
                        onChange={e => setDraft({ ...draft, structure: { ...draft.structure, expiry2_dte: +e.target.value } })} />
                    </Grid>
                  )}

                  {/* FR-UR1/UR2: risk basis — how the platform sizes this. */}
                  <Grid item xs={12}><Divider textAlign="left"><Typography variant="caption">Risk basis (sizing)</Typography></Divider></Grid>
                  <Grid item xs={6} sm={3}>
                    <FormControl fullWidth size="small"><InputLabel>risk basis</InputLabel>
                      <Select label="risk basis" value={draft.structure?.risk_basis?.type || templateMeta?.defaultRiskBasis?.type || 'max_loss'}
                        onChange={e => setDraft({ ...draft, structure: { ...draft.structure, risk_basis: { ...(draft.structure?.risk_basis || {}), type: e.target.value } } })}>
                        {(catalog.riskBases || ['max_loss', 'collateral', 'stress_loss', 'margin_estimate']).map(b =>
                          <MenuItem key={b} value={b}>{b}</MenuItem>)}
                      </Select></FormControl>
                  </Grid>
                  {riskBasisType === 'stress_loss' && (
                    <>
                      <Grid item xs={6} sm={3}>
                        <TextField fullWidth size="small" type="number" label="stress move %"
                          value={draft.structure?.risk_basis?.move?.value ?? templateMeta?.defaultRiskBasis?.move?.value ?? -20}
                          onChange={e => setDraft({ ...draft, structure: { ...draft.structure,
                            risk_basis: { ...draft.structure?.risk_basis, type: 'stress_loss', move: { value: +e.target.value, unit: 'percent' } } } })} />
                      </Grid>
                      <Grid item xs={6} sm={3}>
                        <TextField fullWidth size="small" type="number" label="horizon (days)"
                          value={draft.structure?.risk_basis?.horizon_days ?? templateMeta?.defaultRiskBasis?.horizon_days ?? 5}
                          onChange={e => setDraft({ ...draft, structure: { ...draft.structure,
                            risk_basis: { ...draft.structure?.risk_basis, type: 'stress_loss', horizon_days: +e.target.value } } })} />
                      </Grid>
                    </>
                  )}

                  {/* FR-UR3: eligibility + FR-UR4: mandatory risk exit */}
                  {templateMeta?.requires && Object.keys(templateMeta.requires).length > 0 && (
                    <Grid item xs={12}>
                      <Alert severity={templateMeta.requires.undefined_risk ? 'warning' : 'info'} sx={{ py: 0.5 }}>
                        Requires: {Object.entries(templateMeta.requires).map(([k, v]) => v === true ? k : `${k} ≥ ${v}`).join(' · ')}
                      </Alert>
                    </Grid>
                  )}
                  {isUndefinedRisk && !hasRiskExit && !ackNoStop && (
                    <Grid item xs={12}>
                      <Alert severity="error" sx={{ py: 0.5 }}>
                        FR-UR4: undefined-risk structures need a risk exit (stop, delta_exit, or time_exit) in Exits — or record an explicit "no stop" acknowledgment below.
                      </Alert>
                    </Grid>
                  )}
                  {isUndefinedRisk && !hasRiskExit && (
                    <Grid item xs={12}>
                      <FormControlLabel
                        control={<Switch size="small" checked={ackNoStop}
                          onChange={e => setDraft({ ...draft,
                            acknowledgments: e.target.checked
                              ? [...new Set([...(draft.acknowledgments || []), 'undefined_risk_no_stop'])]
                              : (draft.acknowledgments || []).filter(a => (typeof a === 'string' ? a : a?.id) !== 'undefined_risk_no_stop') })} />}
                        label="I acknowledge this structure has undefined risk and I am running it with NO stop" />
                    </Grid>
                  )}

                  {/* FR-ST4: custom structure editor — up to 8 legs */}
                  {isCustom && (
                    <Grid item xs={12}>
                      <CustomLegsEditor spec={draft} onChange={setDraft} />
                    </Grid>
                  )}
                </Grid>
              </AccordionDetails>
            </Accordion>

            <Accordion>
              <AccordionSummary expandIcon={<ExpandMoreIcon />}><Typography fontWeight={600}>Exit rules</Typography></AccordionSummary>
              <AccordionDetails><ExitsEditor spec={draft} onChange={setDraft} /></AccordionDetails>
            </Accordion>
          </CardContent>
        </Card>
      )}

      {/* ── Decision tree (v6 Steps B+C): outline view + Run Test overlay ── */}
      {detail && tree?.tree && (
        <Card sx={{ mb: 3 }}>
          <CardContent>
            <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 1, flexWrap: 'wrap', gap: 1 }}>
              <Typography variant="h6">Decision tree
                {tree.migrated && <Chip size="small" variant="outlined" label="migrated view" sx={{ ml: 1 }} />}
                {equiv && equiv.status !== 'n/a' && (
                  <Chip size="small" sx={{ ml: 1 }}
                    color={equiv.status === 'passed' ? 'success' : 'error'}
                    label={`equivalence: ${equiv.status} (${equiv.days}d)`} />
                )}
                {equiv?.status === 'n/a' && (
                  <Chip size="small" variant="outlined" sx={{ ml: 1 }}
                    label="equivalence: n/a" title={equiv.reason} />
                )}
              </Typography>
              <Box sx={{ display: 'flex', gap: 1, alignItems: 'center', flexWrap: 'wrap' }}>
                <Select size="small" value={treeInstId} displayEmpty sx={{ minWidth: 150 }}
                  onChange={e => setTreeInstId(e.target.value)}>
                  <MenuItem value="__defaults__">strategy defaults</MenuItem>
                  {(detail.instances || []).map(i => <MenuItem key={i.id} value={i.id}>{i.name}</MenuItem>)}
                </Select>
                <TextField size="small" type="date" value={evalDate} InputLabelProps={{ shrink: true }}
                  onChange={e => setEvalDate(e.target.value)} />
                <Button size="small" variant="contained" startIcon={<PlayIcon />}
                  disabled={busyInst === -1 || !treeInstId} onClick={runTreeTest}>
                  {busyInst === -1 ? 'Running…' : 'Run Test'}</Button>
                <Button size="small" variant="outlined"
                  disabled={busyInst === -2 || !treeInstId || treeInstId === '__defaults__'} onClick={runTreeBacktest}>
                  {busyInst === -2 ? 'Testing…' : 'Range test (30d)'}</Button>
                <Button size="small" variant="outlined"
                  disabled={busyInst === -3 || !!equivJob} onClick={runEquivalence}>
                  {busyInst === -3 ? `checking ${equivJob?.done ?? 0}/${equivJob?.total ?? 60}` : 'Equivalence (60d)'}</Button>
              </Box>
            </Box>
            {treeTest?.evaluation?.as_of && (
              <Typography variant="caption" color="text.secondary" display="block" sx={{ mb: 0.5 }}>
                TEST — evaluated as of {new Date(treeTest.evaluation.as_of).toLocaleString('en-US',
                  { timeZone: 'America/New_York', month: 'numeric', day: 'numeric', year: 'numeric',
                    hour: 'numeric', minute: '2-digit' })} ET
                {treeTest.data_sources?.chain === 'MODELED' ? ' · MODELED pricing'
                  : treeTest.data_sources?.chain ? ` · chain: ${treeTest.data_sources.chain}` : ''}
                {treeTest.data_sources?.price_anchor
                  ? ` · anchor: ${treeTest.data_sources.price_anchor}` : ''}
                {treeTest.data_sources?.spot ? ` · spot: ${treeTest.data_sources.spot}` : ''}
                {treeTest.data_sources?.vix ? ` · vix: ${treeTest.data_sources.vix}` : ''}
                ; taken path highlighted, untaken branches dimmed. No signal was emitted.
              </Typography>
            )}
            {treeTest?.decision && treeTest.reason_code !== 'MARKET_CLOSED' && (
              <Chip size="small" clickable sx={{ mb: 0.75 }}
                color={treeTest.decision === 'ENTER' ? 'success' : 'warning'}
                label={`${treeTest.decision === 'ENTER' ? 'Enter' : 'Skipped'}`
                  + `${treeTest.log?.decision?.message ? `: ${treeTest.log.decision.message}` : ''}`
                  + `${treeTest.reason_code ? ` (${treeTest.reason_code})` : ''}`}
                onClick={() => {
                  const failId = [...(treeTest.trace || [])].reverse()
                    .find(t => ['failed', 'skipped', 'no'].includes(t.result))?.step_id;
                  const el = failId && document.querySelector(`[data-step-id="${CSS.escape(failId)}"]`);
                  el?.scrollIntoView({ behavior: 'smooth', block: 'center' });
                }} />
            )}
            {treeTest?.reason_code === 'MARKET_CLOSED' && (
              <Alert severity="info" sx={{ mb: 1 }}>
                {treeTest.detail?.holiday
                  ? `Market holiday: ${treeTest.detail.holiday} (${treeTest.detail.date})`
                  : `Not a trading day (${treeTest.detail?.weekday || 'closed day'}${treeTest.detail?.date ? `, ${treeTest.detail.date}` : ''})`}
                {treeTest.detail?.last_trading_day
                  ? ` — last trading day was ${treeTest.detail.last_trading_day}; pick it in the date field to run the full tree.`
                  : '.'}
                {' '}Nothing was evaluated.
              </Alert>
            )}
            {equiv?.status === 'failed' && equiv.divergences?.length > 0 && (
              <Alert severity="warning" sx={{ mb: 1 }}>
                Spec ↔ tree diverged on {equiv.divergences.length} day(s):{' '}
                {equiv.divergences.slice(0, 8).map(d => `${d.day} (spec ${d.spec} · tree ${d.tree})`).join(' · ')}
                {equiv.divergences.length > 8 ? ` · +${equiv.divergences.length - 8} more` : ''}
              </Alert>
            )}
            {tree.migrated && (tree.coverage || []).length > 0 && (
              <Accordion sx={{ mb: 1, boxShadow: 'none', border: '1px solid', borderColor: 'divider' }}>
                <AccordionSummary expandIcon={<ExpandMoreIcon />} sx={{ minHeight: 36 }}>
                  <Typography variant="subtitle2">
                    Migration coverage — {tree.coverage.filter(c => c.status === 'unmapped').length
                      ? <Chip size="small" color="error" sx={{ ml: 0.5 }}
                          label={`${tree.coverage.filter(c => c.status === 'unmapped').length} unmapped (activation blocked)`} />
                      : 'all spec elements mapped'}
                  </Typography>
                </AccordionSummary>
                <AccordionDetails sx={{ pt: 0 }}>
                  <Table size="small">
                    <TableHead><TableRow>
                      <TableCell>Spec element</TableCell><TableCell>Tree mapping</TableCell><TableCell>Status</TableCell>
                    </TableRow></TableHead>
                    <TableBody>
                      {tree.coverage.map((c, i) => (
                        <TableRow key={i}>
                          <TableCell sx={{ fontFamily: 'monospace' }}>{c.element}</TableCell>
                          <TableCell>{c.mapped_to}</TableCell>
                          <TableCell>
                            <Chip size="small" label={c.status}
                              color={c.status === 'unmapped' ? 'error' : c.status === 'mapped' ? 'success' : 'default'} />
                          </TableCell>
                        </TableRow>
                      ))}
                    </TableBody>
                  </Table>
                </AccordionDetails>
              </Accordion>
            )}
            <StrategyTreeView tree={tree.tree} instances={detail.instances || []}
              trace={treeTest?.trace} result={treeTest}
              strategyId={detail.strategy.id}
              onInstancePatched={() => openStrategy(detail.strategy.id)}
              onSaveVersion={async (treeSpec) => {
                const v = bumpVersion(draftVersion);
                const res = await apiRequest(`/api/strategies/${detail.strategy.id}/versions`, {
                  method: 'POST', body: JSON.stringify({ version: v, spec: treeSpec }),
                });
                if (res.status === 201) {
                  setSuccessMsg(`Saved tree as version ${v}`);
                  setDraftVersion(v);
                  openStrategy(detail.strategy.id);
                } else {
                  setError(res.data?.errors ? JSON.stringify(res.data.errors) : (res.data?.error || 'save failed'));
                }
              }} />
            {treeBt && (
              <Box sx={{ mt: 2 }}>
                <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, flexWrap: 'wrap', mb: 0.5 }}>
                  <Typography variant="subtitle2">
                    Range test — {treeBt.rows?.length ?? 0} trading day(s) at {treeBt.entry_time || 'entry time'}
                  </Typography>
                  {Object.entries(treeBt.summary || {}).map(([k, v]) =>
                    <Chip key={k} size="small" variant="outlined" label={`${k}: ${v}`} />)}
                  {treeBt.diff && (
                    <Chip size="small"
                      color={treeBt.diff.mismatches === 0 ? 'success' : 'warning'}
                      label={`oracle: ${treeBt.diff.matches}/${treeBt.diff.total} match · ${treeBt.diff.mismatches} mismatch · ${treeBt.diff.borderline} borderline`} />
                  )}
                  <Button size="small" component="label" variant="outlined" sx={{ textTransform: 'none', ml: 'auto' }}>
                    {oracleFile ? `oracle: ${oracleFile.name}` : 'Upload oracle CSV'}
                    <input hidden type="file" accept=".csv,text/csv"
                      onChange={e => setOracleFile(e.target.files?.[0] || null)} />
                  </Button>
                </Box>
                <TableContainer sx={{ maxHeight: 300 }}>
                  <Table size="small" stickyHeader>
                    <TableHead><TableRow>
                      <TableCell>Date</TableCell><TableCell>Day</TableCell>
                      <TableCell>Decision</TableCell><TableCell>Reason</TableCell>
                      <TableCell>Expiry</TableCell><TableCell>Width</TableCell>
                      <TableCell>Short put</TableCell><TableCell>Filters</TableCell>
                      {treeBt.diff && <TableCell>Oracle</TableCell>}
                    </TableRow></TableHead>
                    <TableBody>
                      {(treeBt.rows || []).map((d, i) => {
                        const dr = treeBt.diff?.rows?.find(x => x.date === d.date);
                        return (
                          <TableRow key={i} hover sx={{ cursor: 'pointer' }}
                            onClick={() => replayDay(d.date)}>
                            <TableCell sx={{ fontFamily: 'monospace' }}>{d.date}</TableCell>
                            <TableCell>{d.weekday}</TableCell>
                            <TableCell><Chip size="small" label={d.decision}
                              color={d.decision === 'ENTER' ? 'success' : 'default'} /></TableCell>
                            <TableCell>{d.reason || '—'}</TableCell>
                            <TableCell sx={{ fontFamily: 'monospace' }}>{d.expiry || '—'}</TableCell>
                            <TableCell>{d.width ?? '—'}</TableCell>
                            <TableCell sx={{ fontFamily: 'monospace' }}>{d.short_put ?? '—'}</TableCell>
                            <TableCell>
                              {(d.filters || []).map(f =>
                                <Chip key={f.id} size="small" sx={{ mr: 0.25, height: 18, fontSize: 10 }}
                                  color={f.result ? 'default' : 'error'} variant="outlined"
                                  label={`${f.id}: ${f.value ?? '—'}`} />)}
                            </TableCell>
                            {treeBt.diff && (
                              <TableCell>
                                {dr && <Chip size="small" label={dr.status}
                                  color={dr.status === 'match' ? 'success'
                                    : dr.status === 'borderline_mismatch' ? 'warning' : 'error'}
                                  title={(dr.issues || []).join('; ')} />}
                              </TableCell>
                            )}
                          </TableRow>
                        );
                      })}
                    </TableBody>
                  </Table>
                </TableContainer>
                <Typography variant="caption" color="text.secondary">
                  Click a row to replay that day's path on the tree.
                  {treeBt.diff && ' Oracle diff: decision+reason exact; strikes within ±15; borderline rows (filter within 0.1 of threshold) flagged.'}
                </Typography>
              </Box>
            )}
          </CardContent>
        </Card>
      )}

      {/* ── Instances ───────────────────────────────────────────────── */}
      <Card>
        <CardContent>
          <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 1 }}>
            <Typography variant="h6">Instances</Typography>
            <Button variant="contained" size="small" startIcon={<AddIcon />} disabled={!detail}
              onClick={() => {
                setInstForm({ name: '', version_id: detail?.versions?.[0]?.id, api_token_id: tokens[0]?.id,
                  sizing_reference: { account_id: accounts[0]?.id },
                  risk_overrides: { per_trade_cap_pct: 10, aggregate_cap_pct: 40, equity_basis: 'realized' } });
                setInstDialog(true);
              }}>
              New instance</Button>
          </Box>
          <TableContainer>
            <Table size="small">
              <TableHead><TableRow>
                <TableCell>Name</TableCell><TableCell>Mode</TableCell><TableCell>Routing (token)</TableCell>
                <TableCell>Sizing acct</TableCell><TableCell>Enabled</TableCell><TableCell>Last eval</TableCell>
                <TableCell align="right">Actions</TableCell>
              </TableRow></TableHead>
              <TableBody>
                {instances.map(inst => (
                  <TableRow key={inst.id} hover>
                    <TableCell>{inst.name}</TableCell>
                    <TableCell>
                      <Select size="small" value={inst.mode}
                        onChange={e => patchInstance(inst.id, { mode: e.target.value })}>
                        {MODES.map(m => <MenuItem key={m} value={m}>{m}</MenuItem>)}
                      </Select>
                    </TableCell>
                    <TableCell>{tokens.find(t => t.id === inst.api_token_id)?.name || `#${inst.api_token_id}`}</TableCell>
                    <TableCell>{inst.sizing_reference?.account_size_usd != null
                      ? `static $${Number(inst.sizing_reference.account_size_usd).toLocaleString()}`
                      : (accounts.find(a => a.id === inst.sizing_reference?.account_id)?.name || inst.sizing_reference?.account_id || '—')}</TableCell>
                    <TableCell><Switch size="small" checked={!!inst.enabled}
                      onChange={e => patchInstance(inst.id, { enabled: e.target.checked })} /></TableCell>
                    <TableCell>{inst.last_evaluated_at ? new Date(inst.last_evaluated_at).toLocaleString() : '—'}</TableCell>
                    <TableCell align="right" sx={{ whiteSpace: 'nowrap' }}>
                      <TextField size="small" type="date" value={evalDate} sx={{ width: 140, mr: 0.5 }}
                        InputLabelProps={{ shrink: true }}
                        onChange={e => setEvalDate(e.target.value)} />
                      <Tooltip title="Dry-run evaluate (never emits)">
                        <IconButton size="small" disabled={busyInst === inst.id} onClick={() => doEvaluate(inst.id)}>
                          {busyInst === inst.id ? <CircularProgress size={16} /> : <DryRunIcon fontSize="small" />}</IconButton>
                      </Tooltip>
                      <Tooltip title="Run full cycle (evaluate+emit per mode)">
                        <IconButton size="small" disabled={busyInst === inst.id} onClick={() => doRun(inst.id, false)}>
                          <PlayIcon fontSize="small" /></IconButton>
                      </Tooltip>
                      <Tooltip title="Evaluations"><IconButton size="small" onClick={() => openAudit(inst, 'evaluations')}><AuditIcon fontSize="small" /></IconButton></Tooltip>
                      <Tooltip title="Signals"><IconButton size="small" onClick={() => openAudit(inst, 'signals')}><AuditIcon fontSize="small" /></IconButton></Tooltip>
                      <Tooltip title="Logical positions"><IconButton size="small" onClick={() => openAudit(inst, 'positions')}><PositionsIcon fontSize="small" /></IconButton></Tooltip>
                      <Tooltip title="Edit"><IconButton size="small" onClick={() => { setInstForm({ ...inst, parameter_set_id: inst.parameter_set_id || '' }); setInstDialog(true); }}><EditIcon fontSize="small" /></IconButton></Tooltip>
                      <Tooltip title="Delete instance"><IconButton size="small" onClick={() => deleteInstance(inst)}><DeleteIcon fontSize="small" /></IconButton></Tooltip>
                    </TableCell>
                  </TableRow>
                ))}
                {!instances.length && <TableRow><TableCell colSpan={7}>
                  <Typography color="text.secondary">No instances. Select a strategy above, then create one — it defaults to DRY_RUN.</Typography>
                </TableCell></TableRow>}
              </TableBody>
            </Table>
          </TableContainer>

          {evalResult && (
            <Box sx={{ mt: 2 }}>
              <Typography variant="subtitle1" fontWeight={600}>Dry-run result</Typography>
              <EvalResult result={evalResult} />
            </Box>
          )}
          {runResult && (
            <Box sx={{ mt: 2 }}>
              <Typography variant="subtitle1" fontWeight={600}>Run result</Typography>
              {runResult.error
                ? <Alert severity="error">{runResult.error}</Alert>
                : <RunResult result={runResult} />}
            </Box>
          )}
        </CardContent>
      </Card>

      {/* ── Instance dialog ─────────────────────────────────────────── */}
      <Dialog open={instDialog} onClose={() => setInstDialog(false)} maxWidth="sm" fullWidth>
        <DialogTitle>{instForm.id ? 'Edit instance' : 'New instance'}</DialogTitle>
        <DialogContent>
          <Stack spacing={2} sx={{ mt: 1 }}>
            <TextField label="name" size="small" value={instForm.name || ''}
              onChange={e => setInstForm({ ...instForm, name: e.target.value })} />
            <FormControl size="small"><InputLabel>version</InputLabel>
              <Select label="version" value={instForm.version_id || ''}
                onChange={e => setInstForm({ ...instForm, version_id: e.target.value })}>
                {(detail?.versions || []).map(v => <MenuItem key={v.id} value={v.id}>v{v.version} (#{v.id})</MenuItem>)}
              </Select></FormControl>
            <FormControl size="small"><InputLabel>parameter set</InputLabel>
              <Select label="parameter set" value={instForm.parameter_set_id ?? ''}
                onChange={e => setInstForm({ ...instForm, parameter_set_id: e.target.value || null })}>
                <MenuItem value="">— spec defaults —</MenuItem>
                {(detail?.parameterSets || []).map(ps => <MenuItem key={ps.id} value={ps.id}>{ps.name}</MenuItem>)}
              </Select></FormControl>
            <FormControl size="small"><InputLabel>routing profile (api token)</InputLabel>
              <Select label="routing profile (api token)" value={instForm.api_token_id ?? ''}
                onChange={e => setInstForm({ ...instForm, api_token_id: e.target.value })}>
                {tokens.map(t => (
                  <MenuItem key={t.id} value={t.id}>
                    {t.name} {t.paperTradingEnabled ? '· paper' : ''} {t.liveTradingEnabled ? '· live' : ''}
                  </MenuItem>))}
              </Select></FormControl>
            <Box sx={{ display: 'flex', gap: 1, alignItems: 'center' }}>
              <FormControl size="small" sx={{ minWidth: 130 }}><InputLabel>sizing</InputLabel>
                <Select label="sizing"
                  value={instForm.sizing_reference?.account_size_usd != null ? 'static' : 'account'}
                  onChange={e => setInstForm({
                    ...instForm,
                    sizing_reference: e.target.value === 'static'
                      ? { account_size_usd: instForm.sizing_reference?.account_size_usd ?? '' }
                      : { account_id: instForm.sizing_reference?.account_id ?? '' }
                  })}>
                  <MenuItem value="account">live account</MenuItem>
                  <MenuItem value="static">static size</MenuItem>
                </Select></FormControl>
              {instForm.sizing_reference?.account_size_usd != null ? (
                <TextField size="small" type="number" label="account size (USD)" sx={{ flex: 1 }}
                  value={instForm.sizing_reference.account_size_usd}
                  onChange={e => setInstForm({ ...instForm, sizing_reference: { account_size_usd: +e.target.value } })} />
              ) : (
                <FormControl size="small" sx={{ flex: 1 }}><InputLabel>reference account</InputLabel>
                  <Select label="reference account" value={instForm.sizing_reference?.account_id ?? ''}
                    onChange={e => setInstForm({ ...instForm, sizing_reference: { account_id: e.target.value } })}>
                    {accounts.map(a => <MenuItem key={a.id} value={a.id}>{a.name || `${a.apiType || a.api_type} #${a.id}`}</MenuItem>)}
                  </Select></FormControl>
              )}
            </Box>
            <Box sx={{ display: 'flex', gap: 1 }}>
              <TextField size="small" type="number" label="per-trade cap %"
                value={instForm.risk_overrides?.per_trade_cap_pct ?? ''} sx={{ flex: 1 }}
                onChange={e => setInstForm({ ...instForm, risk_overrides: { ...instForm.risk_overrides, per_trade_cap_pct: +e.target.value } })} />
              <TextField size="small" type="number" label="aggregate cap %"
                value={instForm.risk_overrides?.aggregate_cap_pct ?? ''} sx={{ flex: 1 }}
                onChange={e => setInstForm({ ...instForm, risk_overrides: { ...instForm.risk_overrides, aggregate_cap_pct: +e.target.value } })} />
            </Box>
            {instForm.id && (
              <FormControl size="small"><InputLabel>mode</InputLabel>
                <Select label="mode" value={instForm.mode || 'dry_run'}
                  onChange={e => setInstForm({ ...instForm, mode: e.target.value })}>
                  {MODES.map(m => <MenuItem key={m} value={m}>{m}</MenuItem>)}
                </Select></FormControl>
            )}
            {instForm.id && <Alert severity="info" sx={{ py: 0.5 }}>New instances are always created in DRY_RUN; set EMIT only after verifying a dry-run.</Alert>}
          </Stack>
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setInstDialog(false)}>Cancel</Button>
          <Button variant="contained" onClick={saveInstance} disabled={!instForm.name || !instForm.version_id}>Save</Button>
        </DialogActions>
      </Dialog>

      {/* ── Import dialog (FR-J5: preview + conflict before commit) ──── */}
      <Dialog open={importOpen} onClose={() => setImportOpen(false)} maxWidth="md" fullWidth>
        <DialogTitle>Import strategy JSON</DialogTitle>
        <DialogContent>
          <Stack spacing={2} sx={{ mt: 1 }}>
            <TextField multiline minRows={6} fullWidth size="small"
              placeholder='Paste a .strategy.json document, or choose a file below'
              value={importText} onChange={e => { setImportText(e.target.value); setImportPreview(null); }}
              inputProps={{ style: { fontFamily: 'monospace', fontSize: 12 } }} />
            <Box>
              <Button component="label" size="small">Choose file
                <input hidden type="file" accept=".json,application/json"
                  onChange={e => {
                    const f = e.target.files?.[0];
                    if (!f) return;
                    const rd = new FileReader();
                    rd.onload = () => { setImportText(String(rd.result)); setImportPreview(null); };
                    rd.readAsText(f);
                  }} />
              </Button>
              <Button size="small" variant="contained" sx={{ ml: 1 }} disabled={!importText || importBusy}
                onClick={() => doImportPreview(importText)}>
                {importBusy ? 'Validating…' : 'Validate & preview'}</Button>
            </Box>
            {importPreview && !importPreview.ok && importPreview.code !== 'CONFLICT' && (
              <Alert severity="error">
                {importPreview.error}
                {(importPreview.errors || []).map((e, i) =>
                  <div key={i}>{e.path}: {e.error || e}</div>)}
              </Alert>
            )}
            {importPreview && (importPreview.ok || importPreview.code === 'CONFLICT') && importPreview.preview && (
              <Alert severity={importPreview.conflict ? 'warning' : 'info'}>
                <Typography variant="body2">
                  {importPreview.preview.strategy} v{importPreview.preview.version} — template{' '}
                  <b>{importPreview.preview.template}</b>
                  {importPreview.preview.undefined_risk && ' · undefined risk'}
                  {' · '}filters {importPreview.preview.filters} · exits {(importPreview.preview.exits || []).join(', ') || 'none'}
                </Typography>
                <Typography variant="caption" sx={{ fontFamily: 'monospace' }}>
                  {importPreview.preview.content_hash}
                </Typography>
                {(importPreview.preview.warnings || []).map((w, i) =>
                  <div key={i}><Typography variant="caption">{w}</Typography></div>)}
                {importPreview.conflict && (
                  <Typography variant="body2" sx={{ mt: 1 }}>
                    Conflict: '{importPreview.conflict.name}' already exists — import as new strategy, add as a new version, or cancel.
                  </Typography>
                )}
              </Alert>
            )}
          </Stack>
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setImportOpen(false)}>Close</Button>
          {importPreview?.conflict && (
            <>
              <Button onClick={() => doImportCommit('cancel')} disabled={importBusy}>Cancel import</Button>
              <Button onClick={() => doImportCommit('version')} disabled={importBusy}>Add as new version</Button>
              <Button variant="contained" onClick={() => doImportCommit('new')} disabled={importBusy}>Import as new strategy</Button>
            </>
          )}
          {importPreview?.ok && importPreview.dryRun && !importPreview.conflict && (
            <Button variant="contained" onClick={() => doImportCommit()} disabled={importBusy}>
              Import (draft, unattached)</Button>
          )}
        </DialogActions>
      </Dialog>

      {/* ── Audit dialog ────────────────────────────────────────────── */}
      <Dialog open={!!audit} onClose={() => setAudit(null)} maxWidth="lg" fullWidth>
        <DialogTitle>{audit?.title}</DialogTitle>
        <DialogContent>
          <TableContainer>
            <Table size="small">
              <TableHead><TableRow>
                {audit?.type === 'evaluations' && <><TableCell>At</TableCell><TableCell>Stage</TableCell><TableCell>Decision</TableCell><TableCell>Reason</TableCell><TableCell>Signal</TableCell><TableCell></TableCell></>}
                {audit?.type === 'signals' && <><TableCell>Emitted</TableCell><TableCell>Type</TableCell><TableCell>Status</TableCell><TableCell>Logical position</TableCell><TableCell>Signal id</TableCell></>}
                {audit?.type === 'positions' && <><TableCell>Logical id</TableCell><TableCell>State</TableCell><TableCell>Expiry</TableCell><TableCell>Fill</TableCell><TableCell>Qty</TableCell><TableCell>Exit</TableCell></>}
              </TableRow></TableHead>
              <TableBody>
                {(audit?.rows || []).map((r, i) => (
                  <TableRow key={i}>
                    {audit.type === 'evaluations' && <>
                      <TableCell>{new Date(r.as_of).toLocaleString()}</TableCell>
                      <TableCell>{r.stage}</TableCell>
                      <TableCell><Chip size="small" label={r.decision} color={r.decision === 'ENTER' ? 'success' : 'default'} /></TableCell>
                      <TableCell>{r.reason_code || '—'}</TableCell>
                      <TableCell sx={{ fontFamily: 'monospace', fontSize: 11 }}>{r.signal_id || '—'}</TableCell>
                      <TableCell>
                        {r.reason_detail?.trace && (
                          <Tooltip title="Replay on decision tree (FR-R3)">
                            <IconButton size="small" onClick={() => {
                              setTreeTest({ decision: r.decision, reason_code: r.reason_code,
                                trace: r.reason_detail.trace, evaluation: { as_of: r.as_of } });
                              setAudit(null);
                            }}><PlayIcon fontSize="small" /></IconButton>
                          </Tooltip>
                        )}
                      </TableCell>
                    </>}
                    {audit.type === 'signals' && <>
                      <TableCell>{new Date(r.emitted_at).toLocaleString()}</TableCell>
                      <TableCell>{r.type}</TableCell>
                      <TableCell><Chip size="small" label={r.status} color={r.status === 'filled' ? 'success' : 'default'} /></TableCell>
                      <TableCell>{r.logical_position_id}</TableCell>
                      <TableCell sx={{ fontFamily: 'monospace', fontSize: 11 }}>{r.signal_id}</TableCell>
                    </>}
                    {audit.type === 'positions' && <>
                      <TableCell sx={{ fontFamily: 'monospace', fontSize: 11 }}>{r.logical_id}</TableCell>
                      <TableCell><Chip size="small" label={r.state} color={r.state === 'open' ? 'success' : 'default'} /></TableCell>
                      <TableCell>{r.expiry}</TableCell>
                      <TableCell>{r.fill_price ?? '—'}</TableCell>
                      <TableCell>{r.quantity ?? '—'}</TableCell>
                      <TableCell>{r.exit_reason || '—'}</TableCell>
                    </>}
                  </TableRow>
                ))}
                {!audit?.rows?.length && <TableRow><TableCell colSpan={6}><Typography color="text.secondary">No rows.</Typography></TableCell></TableRow>}
              </TableBody>
            </Table>
          </TableContainer>
        </DialogContent>
        <DialogActions><Button onClick={() => setAudit(null)}>Close</Button></DialogActions>
      </Dialog>
    </Box>
  );
}

function bumpVersion(v) {
  const m = String(v || '1.0.0').match(/^(\d+)\.(\d+)\.(\d+)$/);
  return m ? `${m[1]}.${m[2]}.${+m[3] + 1}` : `${v}.1`;
}
